import { api } from 'shared/api/client';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { useQueryClient, useMutation, UseMutationResult } from '@tanstack/react-query';
import { TagContext, UpdateTagContextInput, FindOrCreateTagContextInput, SuccessResponse } from './types';
import { documentTaxonomyConfigQueryKeys } from './document-taxonomy-config';
import { invalidateOnContextRepoint } from './tagCacheInvalidation';

export const tagContextQueryKeys = {
  tagContext: 'TAG_CONTEXT',
} as const;

export function useFindOrCreateTagContext(): UseMutationResult<TagContext, unknown, FindOrCreateTagContextInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: FindOrCreateTagContextInput) => {
      const res = await api.POST('/tag-contexts/find-or-create', { body: input });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument],
      });
    },
  });
}

export function useUpdateTagContext(): UseMutationResult<SuccessResponse, unknown, UpdateTagContextInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tagContextId, documentSuperId, ...tagContextData }: UpdateTagContextInput) => {
      const res = await api.PUT('/tag-contexts/{tagContextId}', {
        params: { path: { tagContextId }, query: { documentSuperId: String(documentSuperId) } },
        body: tagContextData,
      });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [tagContextQueryKeys.tagContext, variables.tagContextId],
      });
      queryClient.invalidateQueries({
        queryKey: [documentTaxonomyConfigQueryKeys.configByDocument],
      });
      invalidateOnContextRepoint(queryClient, variables.documentSuperId);
    },
  });
}
